/**
 * Feature Flags Configuration
 *
 * This file defines all feature flags and their default states per environment.
 * Environment is determined by import.meta.env.MODE (development, production, qa, etc.)
 *
 * To add a new feature flag:
 * 1. Add an entry to FEATURE_FLAGS with a unique key
 * 2. Set the enabled state for each environment
 * 3. Optionally add allowedRoles for future role-based access
 */

import type { Environment, FeatureFlagDefinition } from "./types";

/**
 * Central feature flag definitions
 * Add new features here with their environment-specific defaults
 */
export const FEATURE_FLAGS: Record<string, FeatureFlagDefinition> = {
    "add-feature": {
        name: "add-feature",
        description: "Add Feature button in the work product hierarchy sidebar",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "add-user-story": {
        name: "add-user-story",
        description: "Add User Story button in the work product hierarchy sidebar",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "integrate-jira": {
        name: "integrate-jira",
        description: "Add jira integration button in the Integrations page",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "integrate-github": {
        name: "integrate-github",
        description: "Add github integration button in the Integrations page",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "integrate-gitlab": {
        name: "integrate-gitlab",
        description: "Add gitlab integration button in the Integrations page",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "integrate-bitbucket": {
        name: "integrate-bitbucket",
        description: "Add bitbucket integration button in the Integrations page",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "integrate-azure": {
        name: "integrate-azure",
        description: "Add Azure DevOps integration button in the Integrations page",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "edit-product": {
        name: "edit-product",
        description: "Edit product pencil icon on product cards in Product Hub",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    },
    "products-dashboard-v2": {
        name: "products-dashboard-v2",
        description: "New Products dashboard (Grid + List views, Code Nexus layout) — V1 Phase 2",
        environments: {
            development: true,
            qa: true,
            sandbox: true,
            app: true,
            production: true
        }
    }
};

// Type-safe feature flag names derived from configuration
export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

/**
 * Get current environment from Vite
 *
 * Priority:
 * 1. VITE_APP_ENV - Explicit environment override (set in .env files)
 * 2. import.meta.env.MODE - Vite's build mode
 *
 * This allows deployment scripts that copy .env.{env} to .env.production
 * to still correctly identify the target environment.
 */
export function getCurrentEnvironment(): Environment {
    // First check for explicit environment variable (used in deployment)
    const appEnv = (import.meta.env as Record<string, string | undefined>).VITE_APP_ENV;
    if (appEnv) {
        const validEnvs: Environment[] = ["development", "production", "qa", "sandbox", "app"];
        if (validEnvs.includes(appEnv as Environment)) {
            return appEnv as Environment;
        }
    }

    // Fall back to Vite mode (used in local development)
    const mode = import.meta.env.MODE;
    const modeMapping: Record<string, Environment> = {
        development: "development",
        production: "production",
        qa: "qa",
        sandbox: "sandbox",
        app: "app"
    };

    return modeMapping[mode] || "production"; // Default to production for safety
}

/**
 * Check for environment variable overrides
 * Pattern: VITE_FF_<FEATURE_NAME_UPPERCASE>=true|false
 * Example: VITE_FF_EXAMPLE_FEATURE=true
 */
function getEnvOverride(featureName: string): boolean | undefined {
    const envKey = `VITE_FF_${featureName.toUpperCase().replace(/-/g, "_")}`;
    const envValue = (import.meta.env as Record<string, string | undefined>)[envKey];

    if (envValue === "true") return true;
    if (envValue === "false") return false;
    return undefined;
}

/**
 * Check if a feature is enabled for the current environment
 * Checks environment variable overrides first, then falls back to config
 */
export function isFeatureEnabled(featureName: string): boolean {
    // Check environment variable override first
    const override = getEnvOverride(featureName);
    if (override !== undefined) {
        return override;
    }

    // Fall back to config-based check
    const flag = FEATURE_FLAGS[featureName];
    if (!flag) {
        console.warn(`Feature flag "${featureName}" not found`);
        return false;
    }

    const env = getCurrentEnvironment();
    return flag.environments[env] ?? false;
}

/**
 * Get a feature flag definition by name
 */
export function getFeatureFlag(featureName: string): FeatureFlagDefinition | undefined {
    return FEATURE_FLAGS[featureName];
}

/**
 * Get all feature flags with their current enabled state
 */
export function getAllFeatureFlags(): Record<string, boolean> {
    const flags: Record<string, boolean> = {};
    for (const key of Object.keys(FEATURE_FLAGS)) {
        flags[key] = isFeatureEnabled(key);
    }
    return flags;
}
